import { Reducer } from 'redux'
import { links } from '../productsListing/reducer'
import { ReceivedResellerAuthorizationActionTypes } from './actions'
import {
  AgreementDurationType,
  ContractDurationType,
  CPPOProducts,
  DiscountType,
  MicrosoftResellerDetails,
  OfferEndBuyers,
  ResellerAgreementStatus,
  ResellerInfo,
  Stakeholders,
} from '../../../admin/modules/resaleAuthorizations/reducer'
import {
  ChannelType,
  EulaType,
  Installments,
  MetaData,
  OfferRecipients,
  PriceTypes,
  Dimension,
  AzurePricingType,
  AzureOfferPlan,
} from '../privateOffer/reducer'
import { AgreementApprovers } from '../../../externalApps/modules/cppoResellerAgreement/reducer'
import { PlanType } from '@labrav/react-components'

export type ResaleAuthorizationType = 'CREATED' | 'RECEIVED'

export interface ReceivedResellerAuthorization {
  id: string
  isvName?: string
  isvCloudAccountId?: string
  crmAwsResellerAgreementObjectId: string
  agreementDescription: string
  agreementName: string
  buyerAwsAccountId: string
  agreementStartDate?: string
  agreementExpirationDate: string
  emailCustomMessage: string
  contractDurationInMonthsForEndCustomer?: number
  agreementDurationType: AgreementDurationType
  discountType: DiscountType
  contractDurationType: ContractDurationType
  endCustomerEulaType: EulaType
  endCustomerEulaFiles: string[]
  resellerLicenseAgreementType: EulaType
  resellerLicenseAgreementFiles: string[]
  percentageDiscount?: number
  awsAgreementId: string
  agreementApprovers: AgreementApprovers[]
  agreementRecipients: OfferRecipients[]
  customOfferStakeholders?: Stakeholders[]
  agreementStakeholders: Stakeholders[]
  installmentInfo: Installments[]
  metaData: MetaData[]
  renewal?: boolean
  renewalChannel?: ChannelType | null
  createdAt: string
  status: ResellerAgreementStatus
  resellerName: string
  draft?: boolean
  pricesArray?: [keyof PriceTypes]
  dimensions?: Dimension[]
  companyName?: string
  agreementOrigin?: ResaleAuthorizationType
  offerExtendedStatus?: string
  buyerName?: string
  serviceStartDate?: string
  productName?: string
  productType?: PlanType
  updatedAt?: string
  error?: string
  productId?: string
  receivedOn?: string
  auditJson?: ResaleAuthAuditJson
  cloudProductName?: string
  mppoDescription?: string
  offerRecipients?: OfferRecipients[]
  offerStakeholders: Stakeholders[]
  resellerDetails?: MicrosoftResellerDetails[]
  azurePricingType?: AzurePricingType
  plans?: AzureOfferPlan[]
  beneficiaryId?: string
  variableStartDate?: boolean
  existingContractRenewal?: boolean
  offerStartDate?: string
  acceptBy?: string
  mppoName?: string
  eulaType?: EulaType
  eulaFiles?: string[]
  offerExpirationDate?: string
  sellerId?: string
  labraOfferUrl?: string
  acceptanceLink?: string
  azureMppoId?: string
  offerStatus?: ResellerAgreementStatus
  statusLogs?: MPPOResaleAuthAuditJson
  errorDetails?: {
    errors?: FailedErrorMessage[]
  }
  offerEndBuyers?: OfferEndBuyers[]
  currency?: string
}

export type FailedErrorMessage = {
  message?: string
}

export interface ResaleAuthStages {
  'Authorization created'?: string | null
  'Authorization activated'?: string | null
  'Authorization duration ended'?: string | null
  'Authorization deactivated'?: string | null
  'Authorization completed'?: string | null
  'Authorization creation failed'?: string | null
}

export enum ResaleAuthStageToStatusRelationMapEnum {
  CREATED = 'Authorization created',
  ACTIVE = 'Authorization activated',
  EXPIRED = 'Authorization duration ended',
  COMPLETED = 'Authorization completed',
  DEACTIVATED = 'Authorization deactivated',
  FAILED = 'Authorization creation failed',
}

export interface MPPOResaleAuthStages {
  'Created at Labra'?: string | null
  'Sent to reseller'?: string | null
  'Viewed by reseller'?: string | null
  'Extended to buyer'?: string | null
  'Pending purchase'?: string | null
  'Pending account configuration'?: string | null
  Accepted?: string | null
  Expired?: string | null
  'Deactivation in progress'?: string | null
  Deactivated?: string | null
}

export enum MPPOResaleAuthStageToStatusRelationMapEnum {
  'offerInDraft' = 'Created at Labra',
  'offerCreationInProgress' = 'Created at Labra',
  'offerCreationFailed' = 'Sent to reseller',
  'offerSentReseller' = 'Sent to reseller',
  'offerViewedReseller' = 'Viewed by reseller',
  'offerExtendedToBuyer' = 'Extended to buyer',
  'offerTermsAcceptedBuyer' = 'Pending purchase',
  'offerPurchaseComplete' = 'Pending account configuration',
  'offerAccepted' = 'Accepted',
  'offerExpired' = 'Expired',
  'offerRestrictionInProgress' = 'Deactivation in progress',
  'offerRestricted' = 'Deactivated',
}

export type ResaleAuthAuditJson = {
  resaleAuthCreatedAt: string | null
  resaleAuthCreationFailedAt: string | null
  resaleAuthActivatedAt: string | null
  resaleAuthDeactivatedAt: string | null
  resaleAuthExpiredAt: string | null
  resaleAuthCompletedAt: string | null
}

export type MPPOResaleAuthAuditJson = {
  offerInDraft: string | null
  offerCreationInProgress: string | null
  offerCreationFailed: string | null
  offerSentReseller: string | null
  offerViewedReseller: string | null
  offerExtendedToBuyer: string | null
  offerTermsAcceptedBuyer: string | null
  offerPurchaseComplete: string | null
  offerAccepted: string | null
  offerExpired: string | null
  offerRestrictionInProgress: string | null
  offerRestricted: string | null
  offerAcceptedAt: string | null
  offerExpiredAt: string | null
  offerRestrictionInProgressAt: string | null
  offerRestrictedAt: string | null
}

export type ViewResellerAuthorizationType = ReceivedResellerAuthorization & {
  resellersInfo?: ResellerInfo[]
  products?: CPPOProducts[]
  awsProductId?: string
  serviceStartDate?: string
}

export type ApiResponse = {
  pageNumber?: number
  pageSize?: number
  totalCount?: number
  sortBy?: string
  sortOrder?: string
  links?: links
}

export type ReceivedResellerAuthorizationState = {
  resellerAgreements: ReceivedResellerAuthorization[]
  count: number
  pageNumber: number
  noOfPage: number | null
  pageSize: number
  currentReceivedResellerAuthorizations: {
    [x: string]: ReceivedResellerAuthorization
  }
  activeReceivedResellerAuthorizations: ReceivedResellerAuthorization[]
  selectedActiveReceivedResellerAuthorization: string
} & ApiResponse

const initialState: ReceivedResellerAuthorizationState = {
  resellerAgreements: [],
  count: 0,
  pageNumber: 0,
  noOfPage: null,
  pageSize: 10,
  currentReceivedResellerAuthorizations: {},
  activeReceivedResellerAuthorizations: [],
  selectedActiveReceivedResellerAuthorization: '',
}

type actionType =
  | {
      type: ReceivedResellerAuthorizationActionTypes.SET_RECEIVED_RESALE_AUTHORIZATIONS_DATA
      payload: {
        agreements: {
          resellerAgreements: ReceivedResellerAuthorization[]
          count: number
        }
        pageNumber: number
        pageSize: number
      }
    }
  | {
      type: ReceivedResellerAuthorizationActionTypes.SET_CURRENT_RECEIVED_RESELLER_AUTHORIZATION
      payload: ReceivedResellerAuthorization
    }
  | {
      type: ReceivedResellerAuthorizationActionTypes.CLEAR_CURRENT_RECEIVED_RESELLER_AUTHORIZATION
    }
  | {
      type: ReceivedResellerAuthorizationActionTypes.SET_ACTIVE_RECEIVED_RESALE_AUTHORIZATIONS_DATA
      payload: {
        agreements: ReceivedResellerAuthorization[]
      }
    }
  | {
      type: ReceivedResellerAuthorizationActionTypes.SET_SELECTED_ACTIVE_RECEIVED_RESALE_AUTHORIZATIONS_DATA
      payload: {
        authorizationId: string
      }
    }
  | {
      type: ReceivedResellerAuthorizationActionTypes.CLEAR_ACTIVE_RECEIVED_RESELLER_AUTHORIZATION
    }
  | {
      type: ReceivedResellerAuthorizationActionTypes.CLEAR_RECEIVED_RESALE_AUTHORIZATIONS_DATA
    }

export const reducer: Reducer<
  ReceivedResellerAuthorizationState,
  actionType
> = (state = initialState, action) => {
  switch (action.type) {
    case ReceivedResellerAuthorizationActionTypes.SET_RECEIVED_RESALE_AUTHORIZATIONS_DATA: {
      return {
        ...state,
        resellerAgreements: state.resellerAgreements.concat(
          action.payload.agreements.resellerAgreements
        ),
        count: action.payload.agreements.count,
        pageNumber: action.payload.pageNumber,
        pageSize: action.payload.pageSize,
      }
    }
    case ReceivedResellerAuthorizationActionTypes.SET_CURRENT_RECEIVED_RESELLER_AUTHORIZATION: {
      return {
        ...state,
        currentReceivedResellerAuthorizations: {
          [action.payload.id]: action.payload as ReceivedResellerAuthorization,
        },
      }
    }
    case ReceivedResellerAuthorizationActionTypes.CLEAR_CURRENT_RECEIVED_RESELLER_AUTHORIZATION: {
      return {
        ...state,
        currentReceivedResellerAuthorizations: {},
      }
    }
    case ReceivedResellerAuthorizationActionTypes.SET_ACTIVE_RECEIVED_RESALE_AUTHORIZATIONS_DATA: {
      return {
        ...state,
        activeReceivedResellerAuthorizations: action.payload.agreements,
      }
    }
    case ReceivedResellerAuthorizationActionTypes.SET_SELECTED_ACTIVE_RECEIVED_RESALE_AUTHORIZATIONS_DATA: {
      return {
        ...state,
        selectedActiveReceivedResellerAuthorization:
          action.payload.authorizationId,
      }
    }
    case ReceivedResellerAuthorizationActionTypes.CLEAR_ACTIVE_RECEIVED_RESELLER_AUTHORIZATION: {
      return {
        ...state,
        activeReceivedResellerAuthorizations: [],
        selectedActiveReceivedResellerAuthorization: '',
      }
    }
    case ReceivedResellerAuthorizationActionTypes.CLEAR_RECEIVED_RESALE_AUTHORIZATIONS_DATA: {
      return {
        ...state,
        resellerAgreements: [],
        noOfPage: null,
        count: 0,
      }
    }
    default:
      return state
  }
}
